<HomeNavBar ref="home_navbar"/>
<CreatePortal v-if="active_features.show_portals" :show-trigger="false" />

<section class="home-dashboard-page p-1">
  <div class="home-dashboard-shell pb-extra-desktop">
    <section v-if="can_manage_organization && setup_steps.percent < 100" class="home-dashboard-hero">
      <div  class="home-dashboard-hero-copy">
        <p class="home-dashboard-eyebrow">A home crafted around your work</p>
        <h1 class="text-white fw-bold">Welcome to your {{ workspace_welcome_name }}workspace, {{ user_display_name }}.</h1>
        <div class="home-dashboard-hero-meta mt-2">
          <p class="text-xs mb-0">
            {{ home_dashboard_intro_text }}
          </p>
        </div>
      </div>

      <div
        v-if="show_organisation_enabled && (can_manage_organization || can_manage_workspace)"
        class="home-dashboard-setup-card"
      >
        <div class="home-dashboard-progress-ring" :style="{ '--home-setup-progress': `${setup_steps.percent}%` }">
          <span>{{ setup_steps.completed }}/{{ setup_steps.total }}</span>
          <small class="home-dashboard-progress-label">Ready</small>
        </div>
        <div>
          <strong>Set up your workspace</strong>
          <p class="text-xs">{{ setup_steps.remaining }} {{ setup_steps.remaining === 1 ? 'step' : 'steps' }} remaining</p>
        </div>
        <button v-if="setup_steps.percent < 100" type="button" class="btn btn-light btn-sm fw-semibold home-dashboard-action-btn" @click="navigate_to('/organisation/')">
          Continue setup
          <i class="bi bi-arrow-right ms-1"></i>
        </button>
      </div>
    </section>

    <div class="home-dashboard-title-row">
      <div>
        <div class="home-dashboard-breadcrumb">
          <span>Home</span>
          <i class="bi bi-chevron-right"></i>
          <small>Info</small>
        </div>
      </div>

      <div class="home-dashboard-actions">
        <button
          type="button"
          class="btn btn-light border btn-sm home-dashboard-action-btn"
          :class="{ active: is_customizing_home }"
          @click="toggle_home_customizing"
        >
          <i class="bi bi-sliders"></i>
          {{ is_customizing_home ? 'Done' : 'Customize home' }}
        </button>

        <div class="dropdown">
          <button
            type="button"
            class="btn btn-primary btn-sm fw-bold home-dashboard-action-btn"
            data-bs-toggle="dropdown"
            data-bs-auto-close="outside"
            aria-expanded="false"
          >
            <i class="bi bi-plus-lg"></i>
            Add widget
          </button>
          <div class="dropdown-menu dropdown-menu-end home-widget-menu" @click.stop>
            <button
              v-for="widget in available_home_widgets"
              :key="widget.key"
              type="button"
              class="dropdown-item d-flex align-items-center gap-2"
              @click.stop="toggle_home_widget(widget.key)"
            >
              <i class="bi" :class="is_home_widget_visible(widget.key) ? 'bi-check-square-fill text-primary' : 'bi-square'"></i>
              <span>{{ widget.title }}</span>
            </button>
            <div class="dropdown-divider"></div>
            <button type="button" class="dropdown-item" @click.stop="reset_home_widget_layout">
              <i class="bi bi-arrow-counterclockwise me-2"></i>
              Reset layout
            </button>
          </div>
        </div>
      </div>
    </div>

    <draggable
      v-model="home_widgets"
      item-key="key"
      class="home-dashboard-grid"
      handle=".home-widget-drag"
      :animation="180"
      :disabled="!is_customizing_home"
      @change="persist_home_widget_layout"
    >
      <template #item="{ element }">
        <section class="home-dashboard-widget" :class="[element.size, { 'is-customizing': is_customizing_home }]">
          <header class="home-widget-header">
            <div class="home-widget-heading">
              <button
                type="button"
                class="home-widget-drag"
                :class="{ 'is-active': is_customizing_home }"
                :aria-label="`Move ${element.title}`"
              >
                <i class="bi bi-grip-vertical"></i>
              </button>
              <div class="home-widget-title-row">
                <p class="mb-0">{{ element.title }}</p>
                <small>{{ element.label }}</small>
              </div>
            </div>
            <button
              v-if="element.key === 'platform_launcher'"
              type="button"
              class="home-widget-launcher-action"
              @click="open_launcher_menu"
            >
              Open launcher
              <i class="bi bi-arrow-right"></i>
            </button>
            <div v-else class="dropdown">
              <button
                type="button"
                class="home-widget-more"
                aria-label="Widget options"
                data-bs-toggle="dropdown"
                data-bs-auto-close="outside"
                aria-expanded="false"
              >
                <i class="bi bi-three-dots"></i>
              </button>
              <div class="dropdown-menu dropdown-menu-end home-widget-menu" @click.stop>
                <button
                  v-for="widget in available_home_widgets"
                  :key="`widget-menu-${element.key}-${widget.key}`"
                  type="button"
                  class="dropdown-item d-flex align-items-center gap-2"
                  @click.stop="toggle_home_widget(widget.key)"
                >
                  <i class="bi" :class="is_home_widget_visible(widget.key) ? 'bi-check-square-fill text-primary' : 'bi-square'"></i>
                  <span>{{ widget.title }}</span>
                </button>
                <div class="dropdown-divider"></div>
                <button type="button" class="dropdown-item" @click.stop="reset_home_widget_layout">
                  <i class="bi bi-arrow-counterclockwise me-2"></i>
                  Reset layout
                </button>
              </div>
            </div>
          </header>

          <div v-if="element.key === 'quick_access'" class="home-quick-grid">
            <component
              v-for="card in quick_access_cards"
              :key="`quick-${card.title}`"
              :is="card.href ? 'a' : 'button'"
              v-can-create="card.permission"
              :href="card.href || null"
              type="button"
              class="home-quick-tile"
              @click.prevent="card.href ? navigate_to(card.href) : handle_card_click(card)"
            >
              <span class="home-tile-icon">
                <img v-if="card.icon_image" :src="card.icon_image" :alt="card.title" class="home-tile-icon-image" />
                <i v-else class="bi" :class="card.icon"></i>
              </span>
                <span class="home-tile-copy">
                  <p>{{ card.title }}</p>
                  <small>{{ card.description }}</small>
                </span>
              <i class="bi bi-chevron-right home-tile-arrow"></i>
            </component>
            <div v-if="!quick_access_cards.length" class="home-widget-empty">
              <span class="home-widget-empty-icon"><i class="bi bi-inboxes-fill"></i></span>
              <strong>Nothing here yet</strong>
              <small>Tools you open will appear here, so returning to your work becomes faster.</small>
            </div>
          </div>

          <div v-else-if="element.key === 'marketplace'" class="home-marketplace-widget">
            <div class="home-marketplace-copy">
              <span class="home-marketplace-eyebrow">Shop · Book · Sell</span>
              <h3>{{ marketplace_widget_title }}</h3>
              <p>{{ marketplace_widget_description }}</p>
              <div class="badge home-marketplace-pills">
                <button type="button" @click="navigate_to('/marketplace')">Products</button>
                <button type="button" @click="navigate_to('/marketplace')">Services</button>
                <button type="button" @click="navigate_to(token_exists ? '/my_shop?open_actions=1' : '/login')">Manage shop</button>
              </div>
            </div>

            <div class="home-marketplace-visual" aria-hidden="true">
              <div class="home-marketplace-mini-card is-product">
                <span><img :src="home_feature_icons.products" alt="" class="home-inline-feature-icon" /></span>
                <div>
                  <strong>Products</strong>
                  <small>Browse & buy</small>
                </div>
              </div>
              <div class="home-marketplace-mini-card is-service">
                <span><img :src="home_feature_icons.services" alt="" class="home-inline-feature-icon" /></span>
                <div>
                  <strong>Services</strong>
                  <small>Book a time</small>
                </div>
              </div>
            </div>

            <button type="button" class="home-widget-launcher-action home-widget-footer-action" @click="navigate_to('/marketplace')">
              Explore marketplace
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>

          <div v-else-if="element.key === 'discover_events'" class="home-events-widget">
            <div v-if="get_card_stats('events').length" class="home-metric-grid home-event-stat-grid">
              <div v-for="stat in get_card_stats('events')" :key="`events-${stat.label}`" class="home-metric-tile">
                <strong>{{ format_dashboard_stat_value(stat) }}</strong>
                <small>{{ stat.label }}</small>
              </div>
            </div>
            <div v-if="get_card_stats('events').length" class="home-events-ready-strip d-flex align-items-center fw-semibold mt-1">
              <img :src="home_feature_icons.events" alt="" class="home-inline-feature-icon" />
              Event activity is ready. Keep your workspace calendar moving.
            </div>
            <div v-else class="home-widget-empty">
              <span class="home-widget-empty-icon">
                <img :src="home_feature_icons.events" alt="" class="home-widget-empty-icon-image" />
              </span>
              <strong>No event activity yet</strong>
              <small>Upcoming events and RSVPs will appear here once they are available.</small>
            </div>
            <button type="button" class="home-widget-launcher-action home-widget-footer-action" @click="open_events_widget_action">
              {{ get_card_stats('events').length ? 'Discover events' : 'Create event' }}
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>

          <div v-else-if="element.key === 'public_organizations'" class="home-events-widget">
            <div v-if="is_loading_public_organizations" class="home-widget-empty">
              <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
              <strong>Finding public organisations...</strong>
            </div>
            <div v-else-if="public_organizations.length" class="home-quick-grid">
              <button
                v-for="organization in public_organizations"
                :key="`public-organization-${organization.id}`"
                type="button"
                class="home-quick-tile"
                @click="open_public_organization(organization)"
              >
                <span class="home-tile-icon">
                  <img
                    v-if="organization.logo_url"
                    :src="organization.logo_url"
                    :alt="`${organization.company_name} logo`"
                  >
                  <i v-else class="bi bi-buildings"></i>
                </span>
                <span class="home-tile-copy">
                  <p>{{ organization.company_name }}</p>
                  <small>{{ organization.location }}</small>
                </span>
                <i class="bi bi-chevron-right home-tile-arrow"></i>
              </button>
            </div>
            <div v-else class="home-widget-empty">
              <span class="home-widget-empty-icon"><i class="bi bi-buildings"></i></span>
              <strong>No public organisations available</strong>
              <small>New public organisations will appear here when they become available.</small>
            </div>
            <button type="button" class="home-widget-launcher-action home-widget-footer-action" @click="navigate_to('/organisations')">
              Browse all organisations
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>

          <div v-else-if="element.key === 'community_snapshot' && show_organisation_enabled" class="home-metric-widget">
            <div class="home-metric-grid">
              <div v-for="metric in workspace_metrics" :key="metric.label" class="home-metric-tile">
                <strong>{{ metric.value }}</strong>
                <small>{{ metric.label }}</small>
              </div>
            </div>
            <div v-if="can_manage_organization || can_manage_workspace" class="home-ready-strip d-flex align-items-center fw-semibold mt-1">
              <i class="bi bi-check-circle-fill"></i>
              Community ready. Invite your first members.
            </div>
            <button v-if="can_manage_organization || can_manage_workspace" type="button" class="home-widget-launcher-action home-widget-footer-action" @click="navigate_to('/organisation/')">
              Open community analytics
              <i class="bi bi-arrow-right"></i>
            </button>
          </div>

          <div v-else-if="element.key === 'platform_launcher'" class="home-launcher">
            <div class="home-launcher-intro">
            <span>{{ platform_launcher_intro_text }}</span>
            </div>

            <div class="home-launcher-grid">
              <component
                v-for="card in platform_launcher_cards"
                :key="`launcher-${card.title}`"
                :is="card.href ? 'a' : 'button'"
                v-can-create="card.permission"
                :href="card.href || null"
                type="button"
                class="home-launcher-tile"
              @click.prevent="card.href ? navigate_to(card.href) : handle_card_click(card)"
            >
              <span class="home-tile-icon">
                  <img v-if="card.icon_image" :src="card.icon_image" :alt="card.title" class="home-tile-icon-image" />
                  <i v-else class="bi" :class="card.icon"></i>
                </span>
                <span class="home-tile-copy">
                  <p>{{ card.title }}</p>
                  <small>{{ card.description }}</small>
                </span>
                <span v-if="card.stats?.length" class="home-launcher-stats">
                  <span
                    v-for="stat in card.stats.slice(0, 3)"
                    :key="`${card.title}-${stat.label}`"
                    class="home-launcher-stat"
                    :class="{ 'is-money': stat.format === 'currency' }"
                  >
                    <strong>{{ format_dashboard_stat_value(stat) }}</strong>
                    <small>{{ stat.label }}</small>
                  </span>
                </span>
                <i v-else class="bi bi-chevron-right home-tile-arrow"></i>
              </component>
              <div v-if="!platform_launcher_cards.length" class="home-widget-empty">
                <span class="home-widget-empty-icon">
                  <img :src="home_feature_icons.dashboard" alt="" class="home-widget-empty-icon-image" />
                </span>
                <strong>Nothing here yet</strong>
                <small>More workspace tools will appear here as they become available.</small>
              </div>
            </div>
          </div>

          <div v-else-if="element.key === 'needs_attention' && show_organisation_enabled && can_manage_organization" class="home-attention-list">
            <button v-if="setup_steps.percent < 100" type="button" class="home-attention-item" @click="navigate_to('/organization_members')">
              <span class="home-attention-icon">
              <img :src="home_feature_icons.personal_profile" alt="" class="home-inline-feature-icon" />
              </span>
              <span>
                <strong>Invite members</strong>
                <small>Your community is ready. Let new members in.</small>
              </span>
              <i class="bi bi-chevron-right"></i>
            </button>
            <button v-if="setup_steps.percent < 100" type="button" class="home-attention-item" @click="navigate_to('/organization_members')">
              <span class="home-attention-icon">
                <i class="bi bi-shield-fill-check"></i>
              </span>
              <span>
                <strong>Give users permissions</strong>
                <small>Choose what members can create, edit, and manage.</small>
              </span>
              <i class="bi bi-chevron-right"></i>
            </button>
            <div v-if="setup_steps.percent >= 100" class="home-widget-empty">
              <span class="home-widget-empty-icon is-success"><i class="bi bi-check-lg"></i></span>
              <strong>Nothing needs your attention yet</strong>
              <small>Once your community is active, questions and follow-ups will appear here automatically.</small>
            </div>
          </div>

        </section>
      </template>
    </draggable>
  </div>
</section>
